import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress, 
  Alert,
  Chip,
  Collapse,
  Divider,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from 'axios';
import { API_URL } from './constants';
import {
  getEffectiveBrandFromOverrides,
  hashProductId,
  countrySlugMatches,
  productDetailCountrySlug,
  buildProductDetailPath,
  toSqlDateKey,
  getResolvedProductDisplayImageUrl,
  PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY,
  PRODUCT_FLAG_PRICE_BANNER,
  PRODUCT_FLAG_LABEL_PRICE_BANNER,
  PRODUCT_FLAG_SKU_VISIBILITY,
  PRODUCT_FLAG_LABEL_SKU_VISIBILITY,
  SKU_VISIBILITY_OPTIONS,
  SKU_VISIBILITY_DEFAULT,
  SKU_VISIBILITY_EVERYONE,
  SKU_VISIBILITY_REGISTERED,
  SKU_VISIBILITY_METADATA,
  normalizeSkuVisibility,
  getSkuVisibilityLabel,
  indexOverridesByTitle,
  getOverrideValue,
} from './productUtils';
import { getFlatVariantsFromGroup, sanitizeVariant, extractVariantsFromRaw, uniquePackageVariantsFromRows, formatPackageVariantLabel, extractPackageVariantsFromRaw, extractPackageVariantOffersFromRaw, inferPackSizeFromLabel } from './matchingUtils';
import { 
  Package, 
  Tag, 
  Star, 
  CurrencyDollar, 
  MapPin,
  Storefront,
  Image as ImageIcon
} from '@phosphor-icons/react';
import { Search } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useAuth } from './AuthContext';
import { getStyleTokens } from './productViewProjectConfig';
import DateRangeFilter from './DateRangeFilter';
import useSkuTitleTranslations from './hooks/useSkuTitleTranslations';
import SkuTitleWithTranslate from './components/SkuTitleWithTranslate';

const ADMIN_EDIT_EMAIL = 'dominik@forloop.ai';

const FOR_NEW_USERS_ONLY_CHIP_SX = {
  bgcolor: '#e0f7fa',
  color: '#006064',
  fontWeight: 600,
  border: '1px solid #4dd0e1',
};

const PRICE_BANNER_CHIP_SX = {
  bgcolor: '#fce4ec',
  color: '#880e4f',
  fontWeight: 600,
  border: '1px solid #f48fb1',
};

const SKU_VISIBILITY_CHIP_SX = {
  [SKU_VISIBILITY_EVERYONE]: {
    bgcolor: '#eceff1',
    color: '#37474f',
    fontWeight: 600,
    border: '1px solid #b0bec5',
  },
  [SKU_VISIBILITY_REGISTERED]: {
    bgcolor: '#e8eaf6',
    color: '#283593',
    fontWeight: 600,
    border: '1px solid #9fa8da',
  },
  [SKU_VISIBILITY_METADATA]: {
    bgcolor: '#fff8e1',
    color: '#f57f17',
    fontWeight: 600,
    border: '1px solid #ffe082',
  },
};

const DEFAULT_PRODUCT_CATEGORY_GROUPS = [
  'Devices',
  'Sticks',
  'Device Bundles',
  'Sticks Bundles',
  'Accessories',
  'Gift Cards',
  'Other Nicotine Products',
  'Other',
  'Unknown',
  'Price banner product',
];

function getRowDateKey(item) {
  const raw = item?.updated_at || item?.date;
  if (!raw) return null;
  try {
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

function parsePriceNumeric(raw) {
  if (raw == null || raw === '') return NaN;
  let cleaned = String(raw).replace(/[^\d.,-]/g, '').trim();
  if (!cleaned) return NaN;
  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  if (hasComma && hasDot) cleaned = cleaned.replace(/,/g, '');
  else if (hasComma) cleaned = cleaned.replace(/,/g, '.');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function classifyStockClassFromRaw(raw) {
  const r = (raw ?? '').toString().trim().toLowerCase();
  if (!r) return 'unknown';

  const hasNotInStock =
    r.includes('not in stock') ||
    r.includes('no longer in stock') ||
    /\bnot\s+in\s+stock\b/.test(r);
  const hasOutOfStockPhrase =
    r.includes('out of stock') ||
    r.includes('out-of-stock') ||
    r.includes('out_of_stock') ||
    /\bout\s+of\s+stock\b/.test(r);
  const hasSoldOut = r.includes('sold out') || r.includes('sold-out') || /\bsold\s+out\b/.test(r);

  if (
    hasNotInStock ||
    hasOutOfStockPhrase ||
    hasSoldOut ||
    r.includes('unavailable') ||
    r.includes('not available')
  ) {
    return 'oos';
  }
  if (r.includes('in stock') || r.includes('available') || r.includes('add to cart')) {
    return 'avail';
  }
  return 'unknown';
}

function classifyStockFromItem(item) {
  return classifyStockClassFromRaw(item?.stock_status ?? item?.availability ?? '');
}

const PRODUCT_DESCRIPTION_FIELDS = [
  'description',
  'product_description',
  'short_description',
  'long_description',
];

function getProductDescriptionFromRows(rows) {
  if (!Array.isArray(rows) || !rows.length) return null;
  for (const row of rows) {
    for (const field of PRODUCT_DESCRIPTION_FIELDS) {
      const raw = row?.[field];
      if (raw == null) continue;
      const text = String(raw).trim();
      if (text && text !== 'N/A' && text.toLowerCase() !== 'nan') return text;
    }
  }
  return null;
}

function formatSnapshotPrice(value) {
  if (value == null || !Number.isFinite(value)) return null;
  const rounded = Math.round(value * 100) / 100;
  return parseFloat(rounded.toFixed(2)).toString();
}

function normalizeProductDetailPrices(actual, regular) {
  let normalizedActual = Number.isFinite(actual) && actual > 0 ? actual : 0;
  let normalizedRegular = Number.isFinite(regular) && regular > 0 ? regular : 0;
  if (normalizedRegular <= 0 && normalizedActual > 0) {
    normalizedRegular = normalizedActual;
  }
  if (normalizedRegular > 0 && normalizedActual > normalizedRegular) {
    normalizedActual = normalizedRegular;
  }
  return { actual: normalizedActual, regular: normalizedRegular };
}

function snapshotFromDayRows(dayRows) {
  if (!dayRows.length) return null;

  const actualVals = [];
  const regularVals = [];
  dayRows.forEach((item) => {
    let actual = parsePriceNumeric(item?.actual_price || item?.price || item?.current_price);
    let regular = parsePriceNumeric(item?.regular_price || item?.original_price);
    ({ actual, regular } = normalizeProductDetailPrices(actual, regular));
    if (actual > 0) actualVals.push(actual);
    if (regular > 0) regularVals.push(regular);
  });

  const average = (vals) =>
    vals.length ? vals.reduce((sum, value) => sum + value, 0) / vals.length : null;

  let avgActual = average(actualVals);
  let avgRegular = average(regularVals);

  const stockClasses = dayRows.map(classifyStockFromItem).filter((cls) => cls !== 'unknown');
  const hasAvail = stockClasses.includes('avail');
  const hasOos = stockClasses.includes('oos');
  let stockStatus;
  if (hasAvail && hasOos) stockStatus = 'Mixed (In stock / Out of stock)';
  else if (hasAvail) stockStatus = 'In stock';
  else if (hasOos) stockStatus = 'Out of stock';
  else stockStatus = dayRows[0]?.stock_status || 'N/A';

  const currency = dayRows.find((row) => row.currency)?.currency || dayRows[0]?.currency;

  return {
    actual_price: avgActual != null ? formatSnapshotPrice(avgActual) : null,
    regular_price: avgRegular != null ? formatSnapshotPrice(avgRegular) : null,
    currency,
    stock_status: stockStatus,
    pricing_observation_count: dayRows.length,
  };
}

function buildPricingSnapshotForDate(rows, dateKey) {
  if (!dateKey) return null;
  const exactRows = rows.filter((row) => getRowDateKey(row) === dateKey);
  if (exactRows.length) return snapshotFromDayRows(exactRows);

  let bestKey = null;
  rows.forEach((row) => {
    const key = getRowDateKey(row);
    if (!key || key > dateKey) return;
    if (!bestKey || key > bestKey) bestKey = key;
  });
  if (!bestKey) return null;
  return snapshotFromDayRows(rows.filter((row) => getRowDateKey(row) === bestKey));
}

function rowsForPricingDate(rows, dateKey) {
  if (!Array.isArray(rows) || !rows.length) return [];
  if (!dateKey) return rows;
  const exactRows = rows.filter((row) => getRowDateKey(row) === dateKey);
  if (exactRows.length) return exactRows;
  let bestKey = null;
  rows.forEach((row) => {
    const key = getRowDateKey(row);
    if (!key || key > dateKey) return;
    if (!bestKey || key > bestKey) bestKey = key;
  });
  if (!bestKey) return rows;
  return rows.filter((row) => getRowDateKey(row) === bestKey);
}

function rowPackageLabels(row) {
  return extractPackageVariantsFromRaw(row?.package_variants ?? row?.package_variants_data);
}

function rowMatchesPackageLabel(row, label) {
  const key = String(label || '').trim().toLowerCase();
  if (!key) return false;
  const labels = rowPackageLabels(row);
  if (labels.length === 1 && labels[0].toLowerCase() === key) return true;
  const units = Number(row?.['Units (#)']);
  const labelNum = Number(label);
  return Number.isFinite(units) && Number.isFinite(labelNum) && units === labelNum;
}

function numericFromPackAssignment(packSize, customPackSize) {
  if (packSize === 'other') {
    const n = Number(customPackSize);
    return !Number.isNaN(n) && n > 0 ? n : null;
  }
  const n = Number(packSize);
  return !Number.isNaN(n) && n > 0 ? n : null;
}

function formatUnitPriceValue(price, currency, packSize) {
  const num = parseFloat(price);
  if (!packSize || Number.isNaN(num) || num <= 0) return null;
  return `${(num / packSize).toFixed(2)} ${currency || ''}`.trim();
}

function createPackAssignment(packSize = '', customPackSize = '') {
  return {
    id: `pack-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    packSize,
    customPackSize,
  };
}

function PackAssignmentPrices({ actualPrice, regularPrice, currency, packUnits }) {
  if (!packUnits) return null;
  const currencySuffix = currency ? ` ${currency}` : '';
  return (
    <Box display="flex" gap={3} sx={{ mt: 1.25 }} flexWrap="wrap">
      {actualPrice ? (
        <Typography variant="body2">
          <strong>Actual price:</strong>{' '}
          <span style={{ color: '#FF9800' }}>{actualPrice}{currencySuffix}</span>
        </Typography>
      ) : null}
      {regularPrice ? (
        <Typography variant="body2">
          <strong>Regular price:</strong>{' '}
          <span style={{ color: '#2196F3' }}>{regularPrice}{currencySuffix}</span>
        </Typography>
      ) : null}
      {actualPrice ? (
        <Typography variant="body2">
          <strong>Unit actual price:</strong>{' '}
          <span style={{ color: '#FF9800' }}>
            {formatUnitPriceValue(actualPrice, currency, packUnits) ?? '—'}
          </span>
          <span style={{ color: '#aaa', fontSize: '0.7rem', marginLeft: 4 }}>
            ({actualPrice} ÷ {packUnits})
          </span>
        </Typography>
      ) : null}
      {regularPrice ? (
        <Typography variant="body2">
          <strong>Unit regular price:</strong>{' '}
          <span style={{ color: '#2196F3' }}>
            {formatUnitPriceValue(regularPrice, currency, packUnits) ?? '—'}
          </span>
          <span style={{ color: '#aaa', fontSize: '0.7rem', marginLeft: 4 }}>
            ({regularPrice} ÷ {packUnits})
          </span>
        </Typography>
      ) : null}
    </Box>
  );
}

function formatPackSizeLabel(packSize, customPackSize) {
  const n = numericFromPackAssignment(packSize, customPackSize);
  return n ? `${n}-pack` : 'Not set';
}

function PackSizePicker({ options, selectedPackSize, customPackSize, onSelect, onCustomChange }) {
  return (
    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
      <ToggleButtonGroup
        value={selectedPackSize === '' || selectedPackSize == null ? null : selectedPackSize}
        exclusive
        onChange={(_, val) => {
          if (val !== null) onSelect(val);
        }}
        size="small"
        sx={{ flexWrap: 'wrap' }}
      >
        {options.map((n) => (
          <ToggleButton key={n} value={n} sx={{ fontSize: '0.72rem', px: 1.25, py: 0.4 }}>
            {n}-pack
          </ToggleButton>
        ))}
        <ToggleButton value="other" sx={{ fontSize: '0.72rem', px: 1.25, py: 0.4 }}>
          Other
        </ToggleButton>
      </ToggleButtonGroup>
      {selectedPackSize === 'other' && (
        <TextField
          size="small"
          type="number"
          label="Units"
          value={customPackSize}
          onChange={(e) => onCustomChange(e.target.value)}
          inputProps={{ min: 1, step: 1 }}
          sx={{ width: 90 }}
        />
      )}
    </Box>
  );
}

function ProductDetailView() {
  const { projectKey, productId, countryCode } = useParams();
  const lastAppliedProductUrlRef = useRef('');
  const appliedUrlFullDateRangeRef = useRef(false);
  const location = useLocation();
  const dateFromUrl = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('date');
    return toSqlDateKey(raw) || null;
  }, [location.search]);
  const navigate = useNavigate();
  const { uiStyle, isAdmin, user } = useAuth();
  const userEmail = user?.email?.toLowerCase() || '';
  const canRecategorize = isAdmin || userEmail === ADMIN_EDIT_EMAIL.toLowerCase();
  const styleTokens = useMemo(() => getStyleTokens(projectKey, uiStyle), [projectKey, uiStyle]);
  const {
    getDisplayTitle: getSkuDisplayTitle,
    isTranslationActive: isSkuTranslationActive,
    isTranslatingTitle: isSkuTitleTranslating,
    toggleSkuTitleTranslation,
  } = useSkuTitleTranslations();
  
  // State for data
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [previewDateRange, setPreviewDateRange] = useState(null);
  const [dateSliderValue, setDateSliderValue] = useState([0, 0]);
  
  // State for filters
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCategoryGroup, setSelectedCategoryGroup] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [filterPackSize, setFilterPackSize] = useState('');  // pack size filter (sticks only)

  const STICKS_CATEGORY_GROUPS = ['Sticks', 'Sticks Bundles'];
  const isSticksCategorySelected = STICKS_CATEGORY_GROUPS.includes(selectedCategoryGroup);
  
  // State for unique values
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);

  // Persisted recategorization overrides (title -> category_group)
  const [categoryOverrides, setCategoryOverrides] = useState({});
  const [recategorizeCategoryGroup, setRecategorizeCategoryGroup] = useState('');
  const [isSavingRecategorization, setIsSavingRecategorization] = useState(false);
  const [recategorizationError, setRecategorizationError] = useState(null);

  const [brandOverrides, setBrandOverrides] = useState({});
  const [customBrands, setCustomBrands] = useState([]);
  const [editBrandValue, setEditBrandValue] = useState('');
  const [isSavingBrand, setIsSavingBrand] = useState(false);
  const [brandEditError, setBrandEditError] = useState(null);
  const [newCustomBrandInput, setNewCustomBrandInput] = useState('');
  const [isSavingCustomBrand, setIsSavingCustomBrand] = useState(false);

  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportType, setReportType] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSnackbar, setReportSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [priceBannerDialogOpen, setPriceBannerDialogOpen] = useState(false);
  const [priceBanners, setPriceBanners] = useState([]);
  const [priceBannersLoading, setPriceBannersLoading] = useState(false);
  const [priceBannersError, setPriceBannersError] = useState(null);
  const [selectedPriceBanner, setSelectedPriceBanner] = useState(null);
  const [priceBannerTitle, setPriceBannerTitle] = useState('');
  const [priceBannerPrice, setPriceBannerPrice] = useState('');
  const [priceBannerCurrency, setPriceBannerCurrency] = useState('');
  const [generatingPriceBanner, setGeneratingPriceBanner] = useState(false);
  
  // State for selected product details
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [pricingAsOfDateKey, setPricingAsOfDateKey] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [productImageSrc, setProductImageSrc] = useState(null);
  
  // State for filtered products count
  const [filteredProductsCount, setFilteredProductsCount] = useState(0);

  // Pack size state
  const PACK_SIZE_OPTIONS = [1, 2, 5, 10, 11];
  const [selectedPackSize, setSelectedPackSize] = useState('');   // number or 'other'
  const [customPackSize, setCustomPackSize] = useState('');
  const [extraPackAssignments, setExtraPackAssignments] = useState([]); // [{ id, packSize, customPackSize }]

  // Pack size overrides (persisted server-side via /jti/pack-size-overrides)
  const [packSizeOverrides, setPackSizeOverrides] = useState({});

  const persistPackSizeOverride = async (title, value) => {
    if (!isAdmin || !title) return;
    const updated = { ...packSizeOverrides, [title]: value };
    setPackSizeOverrides(updated);
    try {
      await axios.post(`${API_URL}/jti/pack-size-overrides`, {
        title,
        pack_size: value,
      });
    } catch (err) {
      console.error('Error saving pack size override:', err);
      setPackSizeOverrides(packSizeOverrides);
    }
  };

  const persistPackAssignments = async (packSize, custom, extras) => {
    if (!selectedProductDetails?.title) return;
    await persistPackSizeOverride(selectedProductDetails.title, {
      packSize,
      customPackSize: custom,
      extraPacks: (extras || []).map((row) => ({
        packSize: row.packSize,
        customPackSize: row.customPackSize,
      })),
    });
  };

  const savePackSizeOverride = async (packSize, custom) => {
    setSelectedPackSize(packSize);
    setCustomPackSize(custom);
    await persistPackAssignments(packSize, custom, extraPackAssignments);
  };

  // Variant overrides (persisted in localStorage): { [title]: string[] }
  const VARIANT_OVERRIDES_KEY = 'jti_variant_overrides';
  const [variantOverrides, setVariantOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem(VARIANT_OVERRIDES_KEY) || '{}'); }
    catch { return {}; }
  });

  // Product flags (persisted server-side via /jti/product-flags)
  const [productFlags, setProductFlags] = useState({});
  // Bundle content overrides (persisted server-side via /jti/bundle-content-overrides)
  const [bundleContentOverrides, setBundleContentOverrides] = useState({});
  // Matching groups: sku -> group names; group name -> skus; group name -> variants (colours/flavours)
  const [matchingSkuToGroups, setMatchingSkuToGroups] = useState(new Map());
  const [matchingGroupToSkus, setMatchingGroupToSkus] = useState(new Map());
  const [matchingGroupToVariants, setMatchingGroupToVariants] = useState(new Map());
  const [matchingGroupToVariantGroupByVariant, setMatchingGroupToVariantGroupByVariant] = useState(new Map()); // groupName -> Map(variant, variantGroupName)
  const [variantGroupsExpanded, setVariantGroupsExpanded] = useState({}); // groupName -> boolean, default collapsed

  useEffect(() => {
    setVariantGroupsExpanded({});
  }, [selectedProductDetails?.title]);

  const currentFlags = selectedProductDetails?.title
    ? (productFlags[selectedProductDetails.title] || {})
    : {};

  const toggleProductFlag = async (flag) => {
    if (!selectedProductDetails?.title) return;
    const title = selectedProductDetails.title;
    const newValue = !currentFlags[flag];
    // Optimistic update
    const updated = {
      ...productFlags,
      [title]: { ...currentFlags, [flag]: newValue },
    };
    setProductFlags(updated);
    try {
      await axios.post(`${API_URL}/jti/product-flags`, { title, flag, value: newValue });
    } catch (err) {
      console.error('Error saving product flag:', err);
      // Revert on failure
      setProductFlags(productFlags);
    }
  };

  const currentSkuVisibility = normalizeSkuVisibility(currentFlags[PRODUCT_FLAG_SKU_VISIBILITY]);

  const setSkuVisibility = async (value) => {
    if (!selectedProductDetails?.title) return;
    const title = selectedProductDetails.title;
    const newVal = normalizeSkuVisibility(value);
    const updated = {
      ...productFlags,
      [title]: { ...currentFlags, [PRODUCT_FLAG_SKU_VISIBILITY]: newVal },
    };
    setProductFlags(updated);
    try {
      await axios.post(`${API_URL}/jti/product-flags`, {
        title,
        flag: PRODUCT_FLAG_SKU_VISIBILITY,
        value: newVal,
      });
    } catch (err) {
      console.error('Error saving SKU visibility:', err);
      setProductFlags(productFlags);
    }
  };

  const setPortfolioType = async (value) => {
    if (!selectedProductDetails?.title) return;
    const title = selectedProductDetails.title;
    const current = currentFlags.portfolioType;
    const newVal = value === current ? null : value; // Toggle off if clicking same
    const updated = {
      ...productFlags,
      [title]: { ...currentFlags, portfolioType: newVal || undefined },
    };
    if (!newVal && updated[title]) {
      const { portfolioType: _, ...rest } = updated[title];
      updated[title] = Object.keys(rest).length ? rest : undefined;
      if (!updated[title]) delete updated[title];
    }
    setProductFlags(updated);
    try {
      if (newVal) {
        await axios.post(`${API_URL}/jti/product-flags`, { title, flag: 'portfolioType', value: newVal });
      } else {
        await axios.delete(`${API_URL}/jti/product-flags`, { params: { title, flag: 'portfolioType' } });
      }
    } catch (err) {
      console.error('Error saving portfolio type:', err);
      setProductFlags(productFlags);
    }
  };


  // Fetch data from the JTI API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dataResponse, overridesResponse, brandOverridesResponse, matchingResponse, flagsResponse, bundleContentResponse, packSizeResponse] = await Promise.all([
          axios.get(`${API_URL}/jti/global-merged-data`),
          axios.get(`${API_URL}/jti/product-category-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/product-brand-overrides`).catch(() => ({ data: { overrides: {}, custom_brands: [] } })),
          axios.get(`${API_URL}/jti/matching-groups`).catch(() => ({ data: { tables: {} } })),
          axios.get(`${API_URL}/jti/product-flags`).catch(() => ({ data: { flags: {} } })),
          axios.get(`${API_URL}/jti/bundle-content-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/pack-size-overrides`).catch(() => ({ data: { overrides: {} } })),
        ]);
        
        const { columns: apiColumns, data: apiData } = dataResponse.data;
        
        // Convert raw data to objects with column names
        const processedData = apiData.map(row => {
          const obj = {};
          apiColumns.forEach((col, index) => {
            obj[col] = row[index];
          });
          const rawDate = obj.updated_at || obj.date;
          if (rawDate) {
            const parsed = dayjs(rawDate);
            obj.__ts = parsed.isValid() ? parsed.valueOf() : null;
          } else {
            obj.__ts = null;
          }
          return obj;
        });
        
        setData(processedData);
        setCategoryOverrides(indexOverridesByTitle(overridesResponse?.data?.overrides || {}));
        setBrandOverrides(brandOverridesResponse?.data?.overrides || {});
        setCustomBrands(brandOverridesResponse?.data?.custom_brands || []);

        const rawDates = processedData
          .map((item) => item.updated_at || item.date)
          .filter((date) => date != null && date !== '')
          .map((date) => dayjs(date))
          .filter((date) => date.isValid())
          .map((date) => date.startOf('day'))
          .sort((a, b) => a.valueOf() - b.valueOf());
        const dates = [];
        const seenDates = new Set();
        rawDates.forEach((date) => {
          const key = date.format('YYYY-MM-DD');
          if (!seenDates.has(key)) {
            seenDates.add(key);
            dates.push(date);
          }
        });
        if (dates.length > 0) {
          const latest = dates[dates.length - 1];
          setAvailableDates(dates);
          setDateRange([latest, latest]);
        } else {
          setAvailableDates([]);
          setDateRange([null, null]);
        }
        setProductFlags(flagsResponse?.data?.flags || {});
        setBundleContentOverrides(bundleContentResponse?.data?.overrides || {});
        const apiPackSize = packSizeResponse?.data?.overrides || {};
        setPackSizeOverrides(apiPackSize);
        // One-time migration from localStorage to API (runs in background)
        if (Object.keys(apiPackSize).length === 0) {
          (async () => {
            try {
              const local = JSON.parse(localStorage.getItem('jti_pack_size_overrides') || '{}');
              if (local && typeof local === 'object' && Object.keys(local).length > 0) {
                for (const [t, v] of Object.entries(local)) {
                  if (t && v) await axios.post(`${API_URL}/jti/pack-size-overrides`, { title: t, pack_size: v }).catch(() => {});
                }
                const migrated = await axios.get(`${API_URL}/jti/pack-size-overrides`).catch(() => ({ data: { overrides: {} } }));
                setPackSizeOverrides(migrated?.data?.overrides || {});
              }
            } catch (_) {}
          })();
        }

        const tables = matchingResponse?.data?.tables || {};
        const skuToGroups = new Map();
        const groupToSkus = new Map();
        const groupToVariants = new Map();
        const groupToVariantGroupByVariant = new Map();
        const addVariantGroupMapping = (groupName, variantGroups) => {
          if (!variantGroups?.length) return;
          if (!groupToVariantGroupByVariant.has(groupName)) groupToVariantGroupByVariant.set(groupName, new Map());
          const map = groupToVariantGroupByVariant.get(groupName);
          variantGroups.forEach((vg) => {
            (vg.variants || []).forEach((v) => {
              const t = String(v).trim();
              if (t) map.set(t, vg.name || '');
            });
          });
        };
        Object.values(tables).forEach((table) => {
          (table?.groups || []).forEach((group) => {
            const name = group?.name;
            const skus = group?.skus || [];
            const variants = getFlatVariantsFromGroup(group);
            if (name && Array.isArray(skus)) {
              if (!groupToSkus.has(name)) groupToSkus.set(name, new Set());
              skus.forEach((sku) => {
                if (sku) {
                  groupToSkus.get(name).add(sku);
                  if (!skuToGroups.has(sku)) skuToGroups.set(sku, new Set());
                  skuToGroups.get(sku).add(name);
                }
              });
              if (variants.length) {
                if (!groupToVariants.has(name)) groupToVariants.set(name, new Set());
                variants.forEach((v) => v && groupToVariants.get(name).add(String(v).trim()));
              }
              addVariantGroupMapping(name, group.variantGroups);
            }
          });
        });
        setMatchingSkuToGroups(skuToGroups);
        setMatchingGroupToSkus(groupToSkus);
        setMatchingGroupToVariants(groupToVariants);
        setMatchingGroupToVariantGroupByVariant(groupToVariantGroupByVariant);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Preselect product from URL (:countryCode/:productId) or navigation state (preselectedProduct)
  useEffect(() => {
    const urlKey = `${countryCode || ''}/${productId || ''}`;
    const fromState = location.state?.preselectedProduct;
    if (fromState && data.length > 0 && data.some((item) => item.title === fromState)) {
      setSelectedProduct(fromState);
      const stateCountry = location.state?.preselectedCountry;
      if (stateCountry) setSelectedCountry(stateCountry);
      lastAppliedProductUrlRef.current = urlKey;
      return;
    }
    if (!productId || data.length === 0) return;
    if (lastAppliedProductUrlRef.current === urlKey && selectedProduct) return;

    const hashMatches = (item) => hashProductId(item.title) === productId;
    const countryMatches = (item) => countrySlugMatches(item.country, countryCode);
    const byHashAndCountry = data.find((item) => hashMatches(item) && countryMatches(item));
    if (byHashAndCountry) {
      setSelectedProduct(byHashAndCountry.title);
      if (byHashAndCountry.country) setSelectedCountry(byHashAndCountry.country);
      lastAppliedProductUrlRef.current = urlKey;
      return;
    }
    const decoded = decodeURIComponent(productId);
    const byTitle =
      data.find((item) => item.title === decoded && countryMatches(item)) ||
      data.find((item) => item.title === decoded);
    if (byTitle) {
      setSelectedProduct(byTitle.title);
      if (byTitle.country) setSelectedCountry(byTitle.country);
      lastAppliedProductUrlRef.current = urlKey;
    }
  }, [data.length, productId, countryCode, location.state?.preselectedProduct, location.state?.preselectedCountry]);

  // Update URL when user selects a product/country (shareable path includes country code)
  // Don't overwrite URL when we have productId in URL but haven't resolved yet (avoids wiping productId before data loads)
  useEffect(() => {
    if (!projectKey) return;
    if (productId && !selectedProduct) return;
    const countryForPath =
      selectedCountry ||
      data.find((item) => item.title === selectedProduct)?.country ||
      countryCode ||
      '';
    const expectedHref = selectedProduct
      ? buildProductDetailPath(projectKey, countryForPath, selectedProduct, dateFromUrl)
      : `/${projectKey}/product-detail`;
    const [expectedPathname, expectedQuery] = expectedHref.split('?');
    const expectedSearch = expectedQuery ? `?${expectedQuery}` : '';
    if (location.pathname !== expectedPathname || (expectedSearch && location.search !== expectedSearch)) {
      lastAppliedProductUrlRef.current = `${productDetailCountrySlug(countryForPath)}/${hashProductId(selectedProduct || '')}`;
      navigate(
        { pathname: expectedPathname, search: expectedSearch || location.search },
        { replace: true }
      );
    }
  }, [selectedProduct, selectedCountry, projectKey, navigate, location.pathname, location.search, productId, data, countryCode, dateFromUrl]);

  const getEffectiveCategoryGroup = (item) => {
    if (!item) return '';
    return getOverrideValue(categoryOverrides, item.title, item.original_title) || item.category_group || '';
  };

  const getEffectiveBrand = (item) => {
    if (!item) return '';
    return getEffectiveBrandFromOverrides(item.title, item.brand, brandOverrides);
  };

  const getEffectivePackSize = (item) => {
    if (!item) return null;
    const saved = packSizeOverrides[item.title];
    if (saved) {
      if (saved.packSize === 'other') {
        const n = Number(saved.customPackSize);
        return !isNaN(n) && n > 0 ? n : null;
      }
      const n = Number(saved.packSize);
      return !isNaN(n) && n > 0 ? n : null;
    }
    const raw = item['Units (#)'];
    const n = raw !== undefined && raw !== null && raw !== '' ? Number(raw) : NaN;
    return !isNaN(n) && n > 0 ? n : null;
  };

  const effectiveDateRange = previewDateRange || dateRange;

  const dateFilteredData = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return data;
    const dateLowerBound = dateRange[0].subtract(1, 'day').valueOf();
    const dateUpperBound = dateRange[1].add(1, 'day').valueOf();
    return data.filter((item) => {
      let ts = item.__ts;
      if (ts === undefined) {
        const parsed = dayjs(item.updated_at || item.date);
        ts = parsed.isValid() ? parsed.valueOf() : null;
      }
      return ts != null && ts > dateLowerBound && ts < dateUpperBound;
    });
  }, [data, dateRange]);

  const findClosestDateIndex = (targetDate) => {
    if (!availableDates.length || !targetDate?.isValid?.()) return -1;
    let closestIdx = -1;
    let smallestDiff = Infinity;
    availableDates.forEach((date, idx) => {
      const diff = Math.abs(date.diff(targetDate, 'millisecond'));
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestIdx = idx;
      }
    });
    return closestIdx;
  };

  useEffect(() => {
    if (!availableDates.length) return;
    const firstDate = availableDates[0];
    const lastDate = availableDates[availableDates.length - 1];
    const startDate = dateRange[0] && dateRange[0].isValid() ? dateRange[0] : firstDate;
    const endDate = dateRange[1] && dateRange[1].isValid() ? dateRange[1] : lastDate;
    const clampedStart = startDate.isBefore(firstDate, 'day') ? firstDate : startDate;
    const clampedEnd = endDate.isAfter(lastDate, 'day') ? lastDate : endDate;
    let startIdx = availableDates.findIndex((d) => d.isSame(clampedStart, 'day'));
    let endIdx = availableDates.findIndex((d) => d.isSame(clampedEnd, 'day'));
    if (startIdx === -1) startIdx = findClosestDateIndex(clampedStart);
    if (endIdx === -1) endIdx = findClosestDateIndex(clampedEnd);
    const safeStart = startIdx === -1 ? 0 : startIdx;
    const safeEnd = endIdx === -1 ? availableDates.length - 1 : endIdx;
    const nextStart = availableDates[safeStart] || firstDate;
    const nextEnd = availableDates[safeEnd] || lastDate;
    if (
      !dateRange[0] ||
      !dateRange[1] ||
      !dateRange[0].isValid() ||
      !dateRange[1].isValid() ||
      !dateRange[0].isSame(nextStart, 'day') ||
      !dateRange[1].isSame(nextEnd, 'day')
    ) {
      setDateRange([nextStart, nextEnd]);
      return;
    }
    setDateSliderValue([Math.min(safeStart, safeEnd), Math.max(safeStart, safeEnd)]);
  }, [availableDates, dateRange]);

  // Arriving from a pinned chart popup (`?date=`): show the full history range so the
  // pinned day is included, and keep that day for "Prices as of".
  useEffect(() => {
    if (!dateFromUrl || !availableDates.length || appliedUrlFullDateRangeRef.current) return;
    appliedUrlFullDateRangeRef.current = true;
    setDateRange([availableDates[0], availableDates[availableDates.length - 1]]);
  }, [dateFromUrl, availableDates]);

  const handleDateSliderChange = (_, newValue) => {
    const nextValue = Array.isArray(newValue) ? newValue : [newValue, newValue];
    const [rawStart, rawEnd] = nextValue;
    const startIdx = Math.min(rawStart, rawEnd);
    const endIdx = Math.max(rawStart, rawEnd);
    setDateSliderValue([startIdx, endIdx]);
    if (!availableDates.length) return;
    const nextStart = availableDates[startIdx];
    const nextEnd = availableDates[endIdx];
    if (!nextStart || !nextEnd) return;
    setPreviewDateRange([nextStart, nextEnd]);
  };

  const handleDateSliderCommit = (_, newValue) => {
    setPreviewDateRange(null);
    if (!availableDates.length) return;
    const [rawStart, rawEnd] = Array.isArray(newValue) ? newValue : [newValue, newValue];
    const startIdx = Math.min(rawStart, rawEnd);
    const endIdx = Math.max(rawStart, rawEnd);
    setDateRange([availableDates[startIdx], availableDates[endIdx]]);
  };

  useEffect(() => {
    if (!dateFilteredData.length) {
      setBrands([]);
      setCountries([]);
      return;
    }
    const scopedRows = selectedCategoryGroup
      ? dateFilteredData.filter(
          (item) => getEffectiveCategoryGroup(item) === selectedCategoryGroup
        )
      : dateFilteredData;
    const fromData = scopedRows.map((item) => getEffectiveBrand(item)).filter(Boolean);
    const fromBrandOverrides = Object.values(brandOverrides || {}).filter(Boolean);
    setBrands(
      [...new Set([...fromData, ...fromBrandOverrides, ...customBrands])].sort()
    );
    setCountries([...new Set(dateFilteredData.map((item) => item.country).filter(Boolean))].sort());
    const defaultCategoryGroups = DEFAULT_PRODUCT_CATEGORY_GROUPS;
    const datasetCategoryGroups = dateFilteredData.map((item) => item.category_group).filter(Boolean);
    const fromCategoryOverrides = Object.values(categoryOverrides || {}).filter(Boolean);
    setCategoryGroups(
      [...new Set([...defaultCategoryGroups, ...datasetCategoryGroups, ...fromCategoryOverrides])]
        .filter((c) => c && c !== 'Starter Kits')
        .sort()
    );
  }, [dateFilteredData, categoryOverrides, brandOverrides, customBrands, selectedCategoryGroup]);

  useEffect(() => {
    if (selectedBrand && brands.length > 0 && !brands.includes(selectedBrand)) {
      setSelectedBrand('');
    }
  }, [brands, selectedBrand]);

  // Same logic as ProductMatchingView "Auto infer": from data columns + from SKU names.
  // From data: variant, product_variants, product_variant_names (extractVariantsFromRaw).
  // From SKU names: strip group name, split on [-–—/&] (not spaces), so "Ruby Twist" stays one variant.
  const getInferredVariants = (title) => {
    if (!title) return [];
    const seen = new Set();
    const groupNames = matchingSkuToGroups.get(title);
    const groupName = groupNames?.size ? [...groupNames][0] : '';
    const skuTitles = groupNames
      ? [...new Set([title, ...[...groupNames].flatMap((gn) => [...(matchingGroupToSkus.get(gn) || [])])])]
      : [title];

    if (groupNames?.size) {
      skuTitles.forEach((sku) => {
        const skuStr = typeof sku === 'string' ? sku : sku?.title || sku;
        if (!skuStr) return;
        dateFilteredData.filter((r) => r.title === skuStr).forEach((item) => {
          [item.variant, item.product_variants, item.product_variant_names].forEach((raw) => {
            extractVariantsFromRaw(raw).forEach((v) => seen.add(v));
          });
        });
      });
    } else {
      dateFilteredData.filter((item) => item.title === title).forEach((item) => {
        [item.variant, item.product_variants, item.product_variant_names].forEach((raw) => {
          extractVariantsFromRaw(raw).forEach((v) => seen.add(v));
        });
      });
    }

    skuTitles.forEach((sku) => {
      const skuStr = typeof sku === 'string' ? sku : sku?.title || sku;
      if (!skuStr) return;
      let rest = skuStr;
      if (groupName) {
        const escaped = groupName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        rest = skuStr.replace(new RegExp(`^${escaped}\\s*[-–—]?\\s*`, 'i'), '').trim();
        if (rest === skuStr) rest = skuStr.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), '').trim();
      }
      if (rest && rest.length > 1) {
        const parts = rest.split(/\s*[-–—/&]\s*/);
        parts.forEach((p) => {
          const t = sanitizeVariant(p);
          if (t && t.length > 1 && !/^\d+$/.test(t)) seen.add(t);
        });
      }
    });

    return [...seen].sort();
  };

  // Match inferred values to group variants; returns group variant names (canonical from matching).
  const matchInferredToGroupVariants = (inferred, groupVariants) => {
    if (!groupVariants?.length) return [];
    const matched = new Set();
    inferred.forEach((v) => {
      const vl = v.toLowerCase();
      const found = groupVariants.find((gv) => {
        const gvl = gv.toLowerCase();
        return vl === gvl || vl.includes(gvl) || gvl.includes(vl);
      });
      if (found) matched.add(found);
    });
    return [...matched].sort();
  };

  // Available variants: if product is in a group with variants defined, use those; else inferred + skus.
  const getAvailableVariants = (title) => {
    if (!title) return [];
    const groupNames = matchingSkuToGroups.get(title);
    const groupVariants = groupNames
      ? [...groupNames].flatMap((gn) => [...(matchingGroupToVariants.get(gn) || [])])
      : [];
    if (groupVariants.length > 0) {
      const saved = variantOverrides[title];
      const fromSaved = saved ? [...saved] : [];
      return [...new Set([...groupVariants, ...fromSaved])].sort();
    }
    const fromData = getInferredVariants(title);
    const fromMatching = new Set();
    if (groupNames) {
      groupNames.forEach((gn) => {
        matchingGroupToSkus.get(gn)?.forEach((sku) => fromMatching.add(sku));
      });
    }
    const saved = variantOverrides[title];
    const fromSaved = saved ? [...saved] : [];
    return [...new Set([...fromData, ...fromMatching, ...fromSaved])].sort();
  };

  // Selected variants. Default: if in group with variants, auto-assign from inferred data; else inferred.
  const getEffectiveVariants = (title) => {
    if (!title) return [];
    const saved = variantOverrides[title];
    if (saved !== undefined) return saved;
    const inferred = getInferredVariants(title);
    const groupNames = matchingSkuToGroups.get(title);
    const groupVariants = groupNames
      ? [...groupNames].flatMap((gn) => [...(matchingGroupToVariants.get(gn) || [])])
      : [];
    if (groupVariants.length > 0) {
      return matchInferredToGroupVariants(inferred, groupVariants);
    }
    return inferred;
  };

  const toggleVariantSelection = (title, variant) => {
    if (!title) return;
    const current = getEffectiveVariants(title);
    const available = getAvailableVariants(title);
    const isSelected = current.includes(variant);
    let next;
    if (isSelected) {
      next = current.filter((v) => v !== variant);
    } else {
      next = [...current, variant].sort();
    }
    const updated = { ...variantOverrides, [title]: next };
    setVariantOverrides(updated);
    try { localStorage.setItem(VARIANT_OVERRIDES_KEY, JSON.stringify(updated)); } catch {}
  };

  const resetVariantSelection = (title) => {
    if (!title) return;
    const updated = { ...variantOverrides };
    delete updated[title];
    setVariantOverrides(updated);
    try { localStorage.setItem(VARIANT_OVERRIDES_KEY, JSON.stringify(updated)); } catch {}
  };

  const productContextRows = useMemo(() => {
    if (!selectedProduct || !dateFilteredData.length) return [];
    let filtered = dateFilteredData.filter((item) => item.title === selectedProduct);
    if (selectedBrand) {
      filtered = filtered.filter((item) => getEffectiveBrand(item) === selectedBrand);
    }
    if (selectedCountry) {
      filtered = filtered.filter((item) => item.country === selectedCountry);
    }
    if (selectedCategoryGroup) {
      filtered = filtered.filter(
        (item) => getEffectiveCategoryGroup(item) === selectedCategoryGroup
      );
    }
    if (filtered.length === 0 && (selectedBrand || selectedCountry)) {
      filtered = dateFilteredData.filter((item) => item.title === selectedProduct);
      if (selectedCategoryGroup) {
        filtered = filtered.filter(
          (item) => getEffectiveCategoryGroup(item) === selectedCategoryGroup
        );
      }
    }
    return filtered;
  }, [dateFilteredData, selectedProduct, selectedBrand, selectedCountry, selectedCategoryGroup, categoryOverrides, brandOverrides]);

  const skuPackageVariants = useMemo(
    () => uniquePackageVariantsFromRows(productContextRows),
    [productContextRows]
  );

  const packagingOffers = useMemo(() => {
    if (!skuPackageVariants.length) return [];
    const dateRows = rowsForPricingDate(productContextRows, pricingAsOfDateKey);
    const structuredByLabel = new Map();
    dateRows.forEach((row) => {
      const offers = extractPackageVariantOffersFromRaw(row?.package_variants ?? row?.package_variants_data);
      offers.forEach((offer) => {
        const key = String(offer.label || '').toLowerCase();
        if (!key) return;
        if (!structuredByLabel.has(key)) {
          structuredByLabel.set(key, {
            label: offer.label,
            units: offer.units,
            actuals: [],
            regulars: [],
            currency: offer.currency,
          });
        }
        const bucket = structuredByLabel.get(key);
        if (offer.units && !bucket.units) bucket.units = offer.units;
        if (offer.currency && !bucket.currency) bucket.currency = offer.currency;
        const actual = parsePriceNumeric(offer.actual_price);
        const regular = parsePriceNumeric(offer.regular_price);
        if (Number.isFinite(actual) && actual > 0) bucket.actuals.push(actual);
        if (Number.isFinite(regular) && regular > 0) bucket.regulars.push(regular);
      });
    });
    const average = (vals) =>
      vals.length ? vals.reduce((sum, value) => sum + value, 0) / vals.length : null;

    return skuPackageVariants.map((label) => {
      const key = label.toLowerCase();
      const structured = structuredByLabel.get(key);
      const matchingRows = dateRows.filter((row) => rowMatchesPackageLabel(row, label));
      const rowSnapshot = matchingRows.length ? snapshotFromDayRows(matchingRows) : null;
      const structuredActual = structured ? average(structured.actuals) : null;
      const structuredRegular = structured ? average(structured.regulars) : null;
      let actual = structuredActual;
      let regular = structuredRegular;
      if (!(Number.isFinite(actual) && actual > 0) && rowSnapshot?.actual_price != null) {
        actual = parsePriceNumeric(rowSnapshot.actual_price);
      }
      if (!(Number.isFinite(regular) && regular > 0) && rowSnapshot?.regular_price != null) {
        regular = parsePriceNumeric(rowSnapshot.regular_price);
      }
      ({ actual, regular } = normalizeProductDetailPrices(actual, regular));
      const units =
        structured?.units ||
        inferPackSizeFromLabel(label) ||
        (matchingRows.length === 1 ? Number(matchingRows[0]['Units (#)']) || null : null);
      return {
        label,
        units: Number.isFinite(units) && units > 0 ? units : null,
        actual_price: actual > 0 ? formatSnapshotPrice(actual) : null,
        regular_price: regular > 0 ? formatSnapshotPrice(regular) : null,
        currency: rowSnapshot?.currency || structured?.currency || null,
        stock_status: rowSnapshot?.stock_status || null,
        observation_count: matchingRows.length || structured?.actuals.length || 0,
      };
    });
  }, [skuPackageVariants, productContextRows, pricingAsOfDateKey]);

  const availablePricingDates = useMemo(() => {
    const keys = new Set();
    productContextRows.forEach((row) => {
      const dateKey = getRowDateKey(row);
      if (dateKey) keys.add(dateKey);
    });
    return [...keys].sort();
  }, [productContextRows]);

  useEffect(() => {
    if (!availablePricingDates.length) {
      setPricingAsOfDateKey(null);
      return;
    }
    const minDate = availablePricingDates[0];
    const maxDate = availablePricingDates[availablePricingDates.length - 1];
    setPricingAsOfDateKey((prev) => {
      if (dateFromUrl) {
        if (dateFromUrl < minDate) return minDate;
        return dateFromUrl;
      }
      if (prev) {
        if (prev < minDate) return minDate;
        if (prev > maxDate) return maxDate;
        return prev;
      }
      return maxDate;
    });
  }, [availablePricingDates, selectedProduct, dateFromUrl]);

  // Update products when filters change
  useEffect(() => {
    // Apply filters (excluding product) to derive the product search list.
    // This way selecting brand/country narrows the product search options.
    let filteredData = [...dateFilteredData];
    if (selectedBrand) {
      filteredData = filteredData.filter((item) => getEffectiveBrand(item) === selectedBrand);
    }
    if (selectedCountry) {
      filteredData = filteredData.filter(item => item.country === selectedCountry);
    }
    if (selectedCategoryGroup) {
      filteredData = filteredData.filter(item => getEffectiveCategoryGroup(item) === selectedCategoryGroup);
    }
    if (filterPackSize !== '' && STICKS_CATEGORY_GROUPS.includes(selectedCategoryGroup)) {
      if (filterPackSize === 'unspecified') {
        filteredData = filteredData.filter(item => getEffectivePackSize(item) === null);
      } else {
        filteredData = filteredData.filter(item => getEffectivePackSize(item) === Number(filterPackSize));
      }
    }

    const candidateProducts = [...new Set(filteredData.map(item => item.title).filter(Boolean))].sort();

    if (productSearchTerm) {
      const searchLower = productSearchTerm.toLowerCase();
      const searchResults = candidateProducts.filter((product) =>
        product.toLowerCase().includes(searchLower)
      );
      setProducts(searchResults);
      setFilteredProductsCount(searchResults.length);
    } else {
      setProducts(candidateProducts);
      setFilteredProductsCount(candidateProducts.length);
    }
    
    // Don't reset product selection if a product is already selected
    // This prevents clearing the selection when filters change
  }, [selectedBrand, selectedCountry, selectedCategoryGroup, filterPackSize, productSearchTerm, dateFilteredData, categoryOverrides, brandOverrides, packSizeOverrides]);

  const productCountryByTitle = useMemo(() => {
    const map = new Map();
    dateFilteredData.forEach((row) => {
      if (row?.title && row?.country && !map.has(row.title)) {
        map.set(row.title, row.country);
      }
    });
    return map;
  }, [dateFilteredData]);

  // Update product details when product context changes (metadata from latest row)
  useEffect(() => {
    if (!productContextRows.length) {
      setSelectedProductDetails(null);
      return;
    }
    const latestRow = [...productContextRows].sort((a, b) => {
      const dateA = getRowDateKey(a) || '';
      const dateB = getRowDateKey(b) || '';
      return dateB.localeCompare(dateA);
    })[0];
    setSelectedProductDetails(latestRow);
  }, [productContextRows]);

  const pricingDetails = useMemo(() => {
    if (!selectedProductDetails) return null;
    if (!pricingAsOfDateKey) return selectedProductDetails;
    const snapshot = buildPricingSnapshotForDate(productContextRows, pricingAsOfDateKey);
    if (!snapshot) {
      return {
        ...selectedProductDetails,
        actual_price: null,
        regular_price: null,
        stock_status: null,
      };
    }
    return { ...selectedProductDetails, ...snapshot };
  }, [selectedProductDetails, productContextRows, pricingAsOfDateKey]);

  const productDescription = useMemo(() => {
    const rows = productContextRows.length
      ? productContextRows
      : selectedProductDetails
        ? [selectedProductDetails]
        : [];
    return getProductDescriptionFromRows(rows) || 'Description not available for this product';
  }, [productContextRows, selectedProductDetails]);

  const isDescriptionMissing = productDescription === 'Description not available for this product';
  const canExpandDescription =
    !isDescriptionMissing &&
    (productDescription.includes('\n') || productDescription.length > 100);

  useEffect(() => {
    setDescriptionExpanded(false);
  }, [selectedProduct]);

  useEffect(() => {
    if (!selectedProductDetails) {
      setRecategorizeCategoryGroup('');
      setRecategorizationError(null);
      return;
    }
    setRecategorizeCategoryGroup(getEffectiveCategoryGroup(selectedProductDetails) || '');
    setRecategorizationError(null);
  }, [selectedProductDetails, categoryOverrides]);

  useEffect(() => {
    if (!selectedProductDetails) {
      setEditBrandValue('');
      setBrandEditError(null);
      return;
    }
    setEditBrandValue(getEffectiveBrand(selectedProductDetails));
    setBrandEditError(null);
  }, [selectedProductDetails, brandOverrides]);

  const brandSelectOptions = useMemo(() => {
    const effective = selectedProductDetails ? getEffectiveBrand(selectedProductDetails) : '';
    const base = [...brands];
    if (effective && !base.includes(effective)) {
      base.push(effective);
    }
    return base.sort();
  }, [brands, selectedProductDetails, brandOverrides]);

  const recategorizeCategoryOptions = useMemo(() => {
    const effective = selectedProductDetails
      ? getEffectiveCategoryGroup(selectedProductDetails)
      : '';
    if (effective && !categoryGroups.includes(effective)) {
      return [...categoryGroups, effective].sort();
    }
    return categoryGroups;
  }, [categoryGroups, selectedProductDetails, categoryOverrides]);

  // Load pack size when product changes: persisted override takes priority over auto-detection
  useEffect(() => {
    if (!selectedProductDetails) {
      setSelectedPackSize('');
      setCustomPackSize('');
      setExtraPackAssignments([]);
      return;
    }
    const saved = packSizeOverrides[selectedProductDetails.title];
    const extras = Array.isArray(saved?.extraPacks)
      ? saved.extraPacks.map((item, index) => ({
          id: `saved-${index}`,
          packSize: item?.packSize ?? '',
          customPackSize: item?.customPackSize || '',
        }))
      : [];
    setExtraPackAssignments(extras);
    if (saved) {
      setSelectedPackSize(saved.packSize);
      setCustomPackSize(saved.customPackSize || '');
      return;
    }
    // Fall back to auto-detection from data field
    const raw = selectedProductDetails['Units (#)'];
    const detected = raw !== undefined && raw !== null && raw !== '' ? Number(raw) : null;
    if (detected && !isNaN(detected) && detected > 0) {
      if (PACK_SIZE_OPTIONS.includes(detected)) {
        setSelectedPackSize(detected);
        setCustomPackSize('');
      } else {
        setSelectedPackSize('other');
        setCustomPackSize(String(detected));
      }
    } else {
      setSelectedPackSize('');
      setCustomPackSize('');
    }
  }, [selectedProductDetails]);

  const handleBrandChange = (event) => {
    setSelectedBrand(event.target.value);
  };

  const handleProductChange = (event) => {
    setSelectedProduct(event.target.value);
  };

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
    // Only reset if no product is selected
    if (!selectedProduct) {
      setSelectedProductDetails(null);
    }
    // Don't clear search term when searching
    if (!productSearchTerm) {
      setProductSearchTerm('');
    }
  };

  const handleCategoryGroupChange = (event) => {
    const newGroup = event.target.value;
    setSelectedCategoryGroup(newGroup);
    if (!STICKS_CATEGORY_GROUPS.includes(newGroup)) {
      setFilterPackSize('');
    }
    // Only reset if no product is selected
    if (!selectedProduct) {
      setSelectedProductDetails(null);
    }
    // Don't clear search term when searching
    if (!productSearchTerm) {
      setProductSearchTerm('');
    }
  };

  const clearAllFilters = () => {
    setSelectedBrand('');
    setSelectedProduct('');
    setSelectedCountry('');
    setSelectedCategoryGroup('');
    setFilterPackSize('');
    setProductSearchTerm('');
    setSelectedProductDetails(null);
  };

  const handleSaveRecategorization = async () => {
    const title = String(selectedProductDetails?.title || '').trim();
    if (!title) return;
    if (!recategorizeCategoryGroup) {
      setRecategorizationError('Please select a category group.');
      return;
    }
    setIsSavingRecategorization(true);
    setRecategorizationError(null);
    try {
      const payload = {
        title,
        category_group: recategorizeCategoryGroup,
      };
      await axios.post(`${API_URL}/jti/product-category-overrides`, payload);
      const originalTitle = String(selectedProductDetails?.original_title || '').trim();
      setCategoryOverrides((prev) => {
        const next = {
          ...(prev || {}),
          [title]: recategorizeCategoryGroup,
          [selectedProductDetails.title]: recategorizeCategoryGroup,
        };
        if (originalTitle) next[originalTitle] = recategorizeCategoryGroup;
        return next;
      });
    } catch (err) {
      console.error('Error saving recategorization:', err);
      setRecategorizationError('Failed to save recategorization.');
    } finally {
      setIsSavingRecategorization(false);
    }
  };

  const handleSaveBrandOverride = async (brandOverride) => {
    if (!selectedProductDetails?.title) return;
    const brand =
      brandOverride !== undefined ? String(brandOverride).trim() : (editBrandValue || '').trim();
    setIsSavingBrand(true);
    setBrandEditError(null);
    try {
      const payload = {
        title: selectedProductDetails.title,
        brand,
      };
      const res = await axios.post(`${API_URL}/jti/product-brand-overrides`, payload);
      setBrandOverrides((prev) => ({
        ...(prev || {}),
        [selectedProductDetails.title]: brand,
      }));
      setEditBrandValue(brand);
      if (Array.isArray(res.data?.custom_brands)) {
        setCustomBrands(res.data.custom_brands);
      }
    } catch (err) {
      console.error('Error saving brand override:', err);
      setBrandEditError('Failed to save brand.');
    } finally {
      setIsSavingBrand(false);
    }
  };

  const handleResetBrandOverride = async () => {
    if (!selectedProductDetails?.title) return;
    setIsSavingBrand(true);
    setBrandEditError(null);
    try {
      const title = selectedProductDetails.title;
      await axios.delete(`${API_URL}/jti/product-brand-overrides`, { params: { title } });
      setBrandOverrides((prev) => {
        const next = { ...(prev || {}) };
        delete next[title];
        return next;
      });
      setEditBrandValue(selectedProductDetails.brand || '');
    } catch (err) {
      console.error('Error removing brand override:', err);
      setBrandEditError('Failed to reset brand to dataset value.');
    } finally {
      setIsSavingBrand(false);
    }
  };

  const handleAddCustomBrand = async () => {
    const brand = (newCustomBrandInput || '').trim();
    if (!brand) {
      setBrandEditError('Enter a brand name to add.');
      return;
    }
    setIsSavingCustomBrand(true);
    setBrandEditError(null);
    try {
      const res = await axios.post(`${API_URL}/jti/custom-brands`, { brand });
      if (Array.isArray(res.data?.custom_brands)) {
        setCustomBrands(res.data.custom_brands);
      } else {
        setCustomBrands((prev) => [...new Set([...prev, brand])].sort());
      }
      setNewCustomBrandInput('');
    } catch (err) {
      console.error('Error adding custom brand:', err);
      setBrandEditError('Failed to add custom brand.');
    } finally {
      setIsSavingCustomBrand(false);
    }
  };

  const handleRemoveCustomBrand = async (brand) => {
    if (!brand) return;
    setIsSavingCustomBrand(true);
    setBrandEditError(null);
    try {
      const res = await axios.delete(`${API_URL}/jti/custom-brands`, { params: { brand } });
      if (Array.isArray(res.data?.custom_brands)) {
        setCustomBrands(res.data.custom_brands);
      } else {
        setCustomBrands((prev) => prev.filter((b) => b !== brand));
      }
    } catch (err) {
      console.error('Error removing custom brand:', err);
      setBrandEditError('Failed to remove custom brand.');
    } finally {
      setIsSavingCustomBrand(false);
    }
  };

  const formatCurrentPriceTypeAnnotations = () => {
    const labels = [];
    if (currentFlags.isStarterKitOrRegistration) labels.push('Starter Kit / Registration Price');
    if (currentFlags.portfolioType === 'Limited edition') labels.push('Limited edition');
    if (currentFlags.isPromoOffer) labels.push('Promo offer');
    if (currentFlags.isForNewUsersOnly) labels.push(PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY);
    if (currentFlags.isCustomFlag) labels.push('Customizable');
    return labels.length ? labels.join(', ') : 'None';
  };

  const buildReportDescriptionTemplate = (type) => {
    const title = selectedProductDetails?.title || '';
    const category = getEffectiveCategoryGroup(selectedProductDetails) || 'N/A';
    const priceTypes = formatCurrentPriceTypeAnnotations();
    if (type === 'wrong_category') {
      return [
        'Report: Wrong category group.',
        `Product: ${title}`,
        `Current category shown: ${category}`,
        '',
        'Expected category:',
      ].join('\n');
    }
    if (type === 'wrong_price_type') {
      return [
        'Report: Wrong price type annotation.',
        `Product: ${title}`,
        `Current annotations: ${priceTypes}`,
        '',
        'Expected annotation:',
      ].join('\n');
    }
    return [
      'Report: Other product data issue.',
      `Product: ${title}`,
      '',
      'Description:',
    ].join('\n');
  };

  const handleOpenReportDialog = (type) => {
    setReportType(type);
    setReportDescription(buildReportDescriptionTemplate(type));
    setReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    if (reportSubmitting) return;
    setReportDialogOpen(false);
    setReportType('');
    setReportDescription('');
  };

  const handleSubmitReport = async () => {
    if (!selectedProductDetails?.title) return;
    setReportSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/jti/support-ticket`,
        {
          product_title: selectedProductDetails.title,
          description: reportDescription.trim() || undefined,
          user_email: user?.email || undefined,
        },
        { withCredentials: true }
      );
      setReportSnackbar({ open: true, message: 'Report submitted successfully.', severity: 'success' });
      handleCloseReportDialog();
    } catch (err) {
      setReportSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to submit report.',
        severity: 'error',
      });
    } finally {
      setReportSubmitting(false);
    }
  };

  const reportDialogTitle =
    reportType === 'wrong_category'
      ? 'Report wrong category'
      : reportType === 'wrong_price_type'
        ? 'Report wrong price type annotation'
        : 'Report other issue';

  const PLACEHOLDER_PRODUCT_IMAGE = 'https://via.placeholder.com/600x400?text=Product+Image';

  const getProductImage = (productData) => {
    if (!productData) return PLACEHOLDER_PRODUCT_IMAGE;
    return getResolvedProductDisplayImageUrl(productData) || PLACEHOLDER_PRODUCT_IMAGE;
  };

  useEffect(() => {
    if (!selectedProductDetails) {
      setProductImageSrc(null);
      return;
    }
    setProductImageSrc(getProductImage(selectedProductDetails));
  }, [selectedProductDetails]);

  const extractProductInfo = (productData) => {
    if (!productData) return {};
    
    // Use actual data fields from the API
    return {
      flavor: productData.flavor_intensity || 'N/A',
      strength: productData.intensity || 'N/A',
      category_group: getEffectiveCategoryGroup(productData) || 'N/A',
      category: productData.clean_category || 'N/A',
      variant: productData.variant || 'N/A'
    };
  };

  const applyGeneratedPriceBannerProduct = (product) => {
    if (!product?.title) return;
    const parsed = dayjs(product.updated_at || product.date);
    const processed = {
      ...product,
      __ts: parsed.isValid() ? parsed.valueOf() : Date.now(),
    };
    setData((prev) => {
      const exists = prev.some(
        (row) =>
          row.id === processed.id ||
          (row.title === processed.title &&
            String(row.country || '').toLowerCase() === String(processed.country || '').toLowerCase())
      );
      return exists
        ? prev.map((row) =>
            row.id === processed.id ||
            (row.title === processed.title &&
              String(row.country || '').toLowerCase() === String(processed.country || '').toLowerCase())
              ? { ...row, ...processed }
              : row
          )
        : [...prev, processed];
    });
    setProductFlags((prev) => ({
      ...prev,
      [processed.title]: { ...(prev[processed.title] || {}), [PRODUCT_FLAG_PRICE_BANNER]: true },
    }));
    setCategoryOverrides((prev) => ({
      ...prev,
      [processed.title]: PRODUCT_FLAG_LABEL_PRICE_BANNER,
    }));
    if (parsed.isValid()) {
      const productDate = parsed.startOf('day');
      setAvailableDates((prev) => {
        const key = productDate.format('YYYY-MM-DD');
        if (prev.some((d) => d.format('YYYY-MM-DD') === key)) return prev;
        return [...prev, productDate].sort((a, b) => a.valueOf() - b.valueOf());
      });
      setDateRange((prev) => {
        if (!prev[0] || !prev[1]) return [productDate, productDate];
        return [
          productDate.isBefore(prev[0], 'day') ? productDate : prev[0],
          productDate.isAfter(prev[1], 'day') ? productDate : prev[1],
        ];
      });
    }
    setSelectedBrand(processed.brand || '');
    setSelectedCountry(processed.country || '');
    setSelectedCategoryGroup(PRODUCT_FLAG_LABEL_PRICE_BANNER);
    setSelectedProduct(processed.title);
    setProductSearchTerm(processed.title);
    if (projectKey) {
      navigate(
        buildProductDetailPath(
          projectKey,
          processed.country,
          processed.title,
          toSqlDateKey(processed.updated_at)
        )
      );
    }
  };

  const openPriceBannerDialog = async () => {
    if (!isAdmin) return;
    setPriceBannerDialogOpen(true);
    setPriceBannersError(null);
    setPriceBannersLoading(true);
    try {
      const params = {};
      if (selectedCountry) params.country = selectedCountry;
      const { data: res } = await axios.get(`${API_URL}/jti/price-banners`, { params });
      const list = res?.banners || [];
      setPriceBanners(list);
      const first = list[0] || null;
      setSelectedPriceBanner(first);
      setPriceBannerTitle(first?.suggested_title || '');
      setPriceBannerPrice(first?.extracted_price != null ? String(first.extracted_price) : '');
      setPriceBannerCurrency(first?.extracted_currency || '');
    } catch (err) {
      setPriceBanners([]);
      setSelectedPriceBanner(null);
      setPriceBannersError(err.response?.data?.detail || 'Failed to load price banners');
    } finally {
      setPriceBannersLoading(false);
    }
  };

  const selectPriceBanner = (banner) => {
    setSelectedPriceBanner(banner);
    setPriceBannerTitle(banner?.suggested_title || '');
    setPriceBannerPrice(banner?.extracted_price != null ? String(banner.extracted_price) : '');
    setPriceBannerCurrency(banner?.extracted_currency || '');
  };

  const handleGeneratePriceBannerProduct = async () => {
    if (!isAdmin || !selectedPriceBanner?.banner_key) return;
    setGeneratingPriceBanner(true);
    try {
      const payload = { banner_key: selectedPriceBanner.banner_key };
      if (priceBannerTitle.trim()) payload.title = priceBannerTitle.trim();
      const parsedPrice = Number(String(priceBannerPrice).replace(',', '.'));
      if (priceBannerPrice !== '' && Number.isFinite(parsedPrice)) payload.actual_price = parsedPrice;
      if (priceBannerCurrency.trim()) payload.currency = priceBannerCurrency.trim();
      const { data: res } = await axios.post(`${API_URL}/jti/price-banner-products`, payload);
      applyGeneratedPriceBannerProduct(res.product);
      setPriceBannerDialogOpen(false);
      setReportSnackbar({
        open: true,
        message: res.created ? 'Price banner product created' : 'Opened existing Price banner product',
        severity: 'success',
      });
    } catch (err) {
      setReportSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to generate Price banner product',
        severity: 'error',
      });
    } finally {
      setGeneratingPriceBanner(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress sx={{ color: styleTokens.accent }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const productInfo = extractProductInfo(selectedProductDetails);

  // Pack size options for the filter dropdown — uses effective pack size so manual overrides appear
  const packSizeFilterOptions = (() => {
    if (!isSticksCategorySelected || !dateFilteredData.length) return [];
    let items = dateFilteredData;
    if (selectedBrand) items = items.filter((i) => getEffectiveBrand(i) === selectedBrand);
    if (selectedCountry) items = items.filter(i => i.country === selectedCountry);
    if (selectedCategoryGroup) items = items.filter(i => getEffectiveCategoryGroup(i) === selectedCategoryGroup);
    return [...new Set(items.map(i => getEffectivePackSize(i)).filter(n => n !== null))].sort((a, b) => a - b);
  })();

  const effectivePackSize = (() => {
    if (selectedPackSize === 'other') {
      const n = Number(customPackSize);
      return !isNaN(n) && n > 0 ? n : null;
    }
    return selectedPackSize ? Number(selectedPackSize) : null;
  })();

  const addPackAssignmentRow = () => {
    if (!isAdmin) return;
    const next = [...extraPackAssignments, createPackAssignment()];
    setExtraPackAssignments(next);
    persistPackAssignments(selectedPackSize, customPackSize, next);
  };

  const updateExtraPackAssignment = (id, packSize, custom) => {
    if (!isAdmin) return;
    const next = extraPackAssignments.map((row) =>
      row.id === id ? { ...row, packSize, customPackSize: custom } : row
    );
    setExtraPackAssignments(next);
    persistPackAssignments(selectedPackSize, customPackSize, next);
  };

  const removeExtraPackAssignment = (id) => {
    if (!isAdmin) return;
    const next = extraPackAssignments.filter((row) => row.id !== id);
    setExtraPackAssignments(next);
    persistPackAssignments(selectedPackSize, customPackSize, next);
  };

  const availableVariants = selectedProductDetails?.title
    ? getAvailableVariants(selectedProductDetails.title)
    : [];
  const effectiveVariants = selectedProductDetails?.title
    ? getEffectiveVariants(selectedProductDetails.title)
    : [];
  const isVariantOverridden = selectedProductDetails?.title
    ? variantOverrides[selectedProductDetails.title] !== undefined
    : false;

  const variantsByGroupName = (() => {
    if (!selectedProductDetails?.title || availableVariants.length === 0) return [];
    const groupNames = matchingSkuToGroups.get(selectedProductDetails.title);
    if (!groupNames?.size) return [{ groupName: '', variants: availableVariants }];
    const byGroup = new Map();
    availableVariants.forEach((v) => {
      const vgName = [...groupNames].map((gn) => matchingGroupToVariantGroupByVariant.get(gn)?.get(v)).find(Boolean) ?? '';
      if (!byGroup.has(vgName)) byGroup.set(vgName, []);
      byGroup.get(vgName).push(v);
    });
    return [...byGroup.entries()].map(([groupName, variants]) => ({ groupName: groupName || '(other)', variants }));
  })();

  // Compact filter styling shared across selection controls (matches Price & Promotions view).
  const compactFilterSx = {
    '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
    '& .MuiInputBase-input': { padding: '4px 8px' },
    '& .MuiSelect-select': {
      fontSize: '0.7rem',
      fontWeight: 400,
      py: 0,
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
  };
  const compactMenuItemSx = { fontSize: '0.8rem' };

  return (
    <>
    <Box p={3}>
      {/* <Alert severity="info" sx={{ mb: 2 }}>
        All data for Ploom, Glo and Iqos in RO, IT, UK will be available on 8th August (data transformations are being handled)
      </Alert>  */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Product Detail View
      </Typography>

      {/* Selection Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
              Select Product
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {selectedBrand && (
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {filteredProductsCount} product{filteredProductsCount !== 1 ? 's' : ''} found
                </Typography>
              )}
              <Button 
                variant="outlined" 
                color="secondary" 
                onClick={clearAllFilters}
                size="small"
                sx={{ fontSize: '0.7rem', py: 0.25 }}
              >
                Clear All Filters
              </Button>
              {isAdmin && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={openPriceBannerDialog}
                  sx={{ fontSize: '0.7rem', py: 0.25 }}
                >
                  Generate from price banner
                </Button>
              )}
            </Box>
          </Box>
                     <Grid container spacing={2}>
             <Grid item xs={12} sm={6} md={3}>
               <DateRangeFilter
                 availableDates={availableDates}
                 effectiveDateRange={effectiveDateRange}
                 dateRange={dateRange}
                 setDateRange={setDateRange}
                 setPreviewDateRange={setPreviewDateRange}
                 dateSliderValue={dateSliderValue}
                 onSliderChange={handleDateSliderChange}
                 onSliderCommit={handleDateSliderCommit}
                 accentColor={styleTokens.accent}
                 minWidth={140}
                 fullWidth
                 sx={compactFilterSx}
               />
             </Grid>
             <Grid item xs={12} sm={6} md={3}>
               <FormControl fullWidth size="small" sx={compactFilterSx}>
                 <InputLabel>Brand</InputLabel>
                 <Select
                   value={selectedBrand}
                   label="Brand"
                   onChange={handleBrandChange}
                 >
                   <MenuItem value="" sx={compactMenuItemSx}>
                     <em>Select a brand</em>
                   </MenuItem>
                   {brands.map((brand) => (
                     <MenuItem key={brand} value={brand} sx={compactMenuItemSx}>
                       {brand}
                     </MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             <Grid item xs={12} sm={6} md={3}>
               <FormControl fullWidth size="small" sx={compactFilterSx}>
                 <InputLabel>Country</InputLabel>
                 <Select
                   value={selectedCountry}
                   label="Country"
                   onChange={handleCountryChange}
                 >
                   <MenuItem value="" sx={compactMenuItemSx}>
                     <em>Select a country</em>
                   </MenuItem>
                   {countries.map((country) => (
                     <MenuItem key={country} value={country} sx={compactMenuItemSx}>
                       {country}
                     </MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             <Grid item xs={12} sm={6} md={3}>
               <Autocomplete
                 fullWidth
                 size="small"
                 options={products}
                 value={selectedProduct}
                 onChange={(event, newValue) => {
                   setSelectedProduct(newValue || '');
                   if (newValue) {
                     setProductSearchTerm(newValue); // Set search term to match selected value
                   }
                 }}
                 onInputChange={(event, newInputValue) => {
                   setProductSearchTerm(newInputValue);
                   if (!newInputValue) {
                     setSelectedProduct(''); // Clear selection when input is cleared
                   }
                 }}
                 inputValue={productSearchTerm}
                 openOnFocus
                 autoHighlight
                 renderInput={(params) => (
                   <TextField
                     {...params}
                     label="Search Products"
                     InputProps={{
                       ...params.InputProps,
                       startAdornment: (
                         <InputAdornment position="start">
                           <Search sx={{ fontSize: '1rem' }} />
                         </InputAdornment>
                       ),
                     }}
                     sx={compactFilterSx}
                   />
                 )}
                 renderOption={(props, option) => (
                   <Box component="li" {...props} sx={compactMenuItemSx}>
                     <SkuTitleWithTranslate
                       title={option}
                       country={productCountryByTitle.get(option)}
                       getDisplayTitle={getSkuDisplayTitle}
                       isTranslationActive={isSkuTranslationActive}
                       isTranslatingTitle={isSkuTitleTranslating}
                       onToggleTranslate={toggleSkuTitleTranslation}
                     />
                   </Box>
                 )}
                 noOptionsText="No products found"
                 freeSolo
                 clearOnEscape
                 selectOnFocus
                 handleHomeEndKeys
               />
             </Grid>
             <Grid item xs={12} sm={6} md={3}>
               <FormControl fullWidth size="small" sx={compactFilterSx}>
                <InputLabel>Category group</InputLabel>
                 <Select
                  value={selectedCategoryGroup}
                  label="Category group"
                  onChange={handleCategoryGroupChange}
                 >
                   <MenuItem value="" sx={compactMenuItemSx}>
                    <em>All category groups</em>
                   </MenuItem>
                  {categoryGroups.map((categoryGroup) => (
                    <MenuItem key={categoryGroup} value={categoryGroup} sx={compactMenuItemSx}>
                      {categoryGroup}
                     </MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             {isSticksCategorySelected && (
               <Grid item xs={12} sm={6} md={3}>
                 <FormControl fullWidth size="small" sx={compactFilterSx}>
                   <InputLabel>Pack size</InputLabel>
                   <Select
                     value={filterPackSize}
                     label="Pack size"
                     onChange={(e) => setFilterPackSize(e.target.value)}
                   >
                     <MenuItem value="" sx={compactMenuItemSx}><em>All pack sizes</em></MenuItem>
                     {packSizeFilterOptions.map((n) => (
                       <MenuItem key={n} value={n} sx={compactMenuItemSx}>{n}-pack</MenuItem>
                     ))}
                     <MenuItem value="unspecified" sx={compactMenuItemSx}><em>Other (no pack size)</em></MenuItem>
                   </Select>
                 </FormControl>
               </Grid>
             )}
           </Grid>
        </CardContent>
      </Card>

      {/* Product Details */}
      {selectedProductDetails ? (
        <Grid container spacing={3}>
          {/* Product Image */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ImageIcon size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Product Image
                </Typography>
                                 <Box 
                   component="img"
                  src={productImageSrc || PLACEHOLDER_PRODUCT_IMAGE}
                   alt={selectedProductDetails.title}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={() => {
                    // Always fall back to a safe placeholder if the image fails to load.
                    if ((productImageSrc || '') !== PLACEHOLDER_PRODUCT_IMAGE) {
                      setProductImageSrc(PLACEHOLDER_PRODUCT_IMAGE);
                    }
                  }}
                   sx={{
                     width: '100%',
                     height: 300,
                     objectFit: 'cover',
                     borderRadius: 1,
                     border: '1px solid #e0e0e0'
                   }}
                 />
              </CardContent>
            </Card>
          </Grid>

          {/* Product Information */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: '#1976d2' }}>
                  <SkuTitleWithTranslate
                    title={selectedProductDetails.title}
                    country={selectedProductDetails.country}
                    getDisplayTitle={getSkuDisplayTitle}
                    isTranslationActive={isSkuTranslationActive}
                    isTranslatingTitle={isSkuTitleTranslating}
                    onToggleTranslate={toggleSkuTitleTranslation}
                  />
                </Typography>
                
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                  <Chip 
                    icon={<Package size={16} />} 
                    label={getEffectiveBrand(selectedProductDetails) || '—'}
                    color="primary" 
                    variant="outlined"
                  />
                  <Chip 
                    icon={<MapPin size={16} />} 
                    label={selectedProductDetails.country}
                    color="secondary" 
                    variant="outlined"
                  />
                  <Chip 
                    icon={<Storefront size={16} />} 
                    label={selectedProductDetails.retailer}
                    color="success" 
                    variant="outlined"
                  />
                  {currentFlags.isStarterKitOrRegistration && (
                    <Chip label="Starter Kit / Registration Price" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, border: '1px solid #ffcc80' }} />
                  )}
                  {currentFlags.portfolioType === 'Limited edition' && (
                    <Chip label="Limited edition" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, border: '1px solid #81c784' }} />
                  )}
                  {currentFlags.isPromoOffer && (
                    <Chip label="Promo offer" size="small" sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 600, border: '1px solid #ce93d8' }} />
                  )}
                  {currentFlags.isForNewUsersOnly && (
                    <Chip label={PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY} size="small" sx={FOR_NEW_USERS_ONLY_CHIP_SX} />
                  )}
                  {(currentFlags[PRODUCT_FLAG_PRICE_BANNER] ||
                    getEffectiveCategoryGroup(selectedProductDetails) === PRODUCT_FLAG_LABEL_PRICE_BANNER) && (
                    <Chip label={PRODUCT_FLAG_LABEL_PRICE_BANNER} size="small" sx={PRICE_BANNER_CHIP_SX} />
                  )}
                  {currentSkuVisibility !== SKU_VISIBILITY_DEFAULT && (
                    <Chip
                      label={`${PRODUCT_FLAG_LABEL_SKU_VISIBILITY}: ${getSkuVisibilityLabel(currentSkuVisibility)}`}
                      size="small"
                      sx={SKU_VISIBILITY_CHIP_SX[currentSkuVisibility]}
                    />
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Product Characteristics */}
                <Typography variant="h6" gutterBottom>
                  Product Characteristics
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Tag size={16} color="#4CAF50" />
                      <Typography variant="body2">
                        <strong>Flavor:</strong> {productInfo.flavor}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Star size={16} color="#FF9800" />
                      <Typography variant="body2">
                        <strong>Strength:</strong> {productInfo.strength}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Package size={16} color="#2196F3" />
                      <Typography variant="body2">
                        <strong>Category group:</strong> {productInfo.category_group || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Tag size={16} color="#9C27B0" />
                      <Typography variant="body2">
                        <strong>Category:</strong> {productInfo.category || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">
                        <strong>{PRODUCT_FLAG_LABEL_SKU_VISIBILITY}:</strong> {getSkuVisibilityLabel(currentSkuVisibility)}
                      </Typography>
                    </Box>
                  </Grid>
                  {['Device Bundles', 'Sticks Bundles'].includes(productInfo.category_group) &&
                    bundleContentOverrides[selectedProductDetails?.title]?.length > 0 && (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="flex-start" gap={1} flexWrap="wrap">
                        <Package size={16} color="#795548" style={{ marginTop: 2, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Bundle content:
                          </Typography>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {(() => {
                              const arr = bundleContentOverrides[selectedProductDetails.title] || [];
                              const counts = {};
                              arr.forEach((item) => {
                                const k = String(item).trim();
                                if (k) counts[k] = (counts[k] || 0) + 1;
                              });
                              return Object.entries(counts).map(([item, n]) => (
                                <Chip
                                  key={item}
                                  label={n > 1 ? `${item} (${n})` : item}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              ));
                            })()}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Variants (colours / flavours) */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Variants (colours / flavours)
                    </Typography>
                    {isVariantOverridden && (
                      <Chip label="overridden" size="small" sx={{ fontSize: '0.62rem', height: 16, bgcolor: '#e3f2fd', color: '#1565c0' }} />
                    )}
                  </Box>

                  <Box>
                    {variantsByGroupName.length > 0 ? (
                      variantsByGroupName.map(({ groupName, variants }) => {
                        const hasEffective = variants.some((v) => effectiveVariants.includes(v));
                        const isExpanded = variantGroupsExpanded[groupName] === true;
                        const displayName = groupName && groupName !== '(other)' ? groupName : 'Variants';
                        return (
                          <Box key={groupName} sx={{ mb: 0.5 }}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => setVariantGroupsExpanded((prev) => ({ ...prev, [groupName]: !prev[groupName] }))}
                            >
                              {isExpanded ? (
                                <ExpandLessIcon fontSize="small" sx={{ color: hasEffective ? 'success.main' : 'text.disabled' }} />
                              ) : (
                                <ExpandMoreIcon fontSize="small" sx={{ color: hasEffective ? 'success.main' : 'text.disabled' }} />
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  color: hasEffective ? 'success.main' : 'text.disabled',
                                  fontWeight: hasEffective ? 600 : 400,
                                }}
                              >
                                {displayName}
                              </Typography>
                            </Box>
                            <Collapse in={isExpanded}>
                              <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center" sx={{ pl: 2.5, mt: 0.5 }}>
                                {variants.map((v) => {
                                  const isSelected = effectiveVariants.includes(v);
                                  return (
                                    <Chip
                                      key={v}
                                      label={v}
                                      size="small"
                                      variant={isSelected ? 'filled' : 'outlined'}
                                      onClick={(e) => { e.stopPropagation(); toggleVariantSelection(selectedProductDetails.title, v); }}
                                      sx={{
                                        cursor: 'pointer',
                                        ...(isSelected ? { bgcolor: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' } : { color: 'text.secondary' }),
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        None detected
                      </Typography>
                    )}
                    {isVariantOverridden && (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ ml: 0.5, fontSize: '0.72rem' }}
                        onClick={() => resetVariantSelection(selectedProductDetails.title)}
                      >
                        Reset to auto
                      </Button>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: 'block' }}>
                    {isVariantOverridden ? 'Click group to expand, chips to toggle. ' : 'Click group to expand, chips to toggle on/off. '}
                    Green = variants found in dataset. Saved in this browser.
                  </Typography>
                </Box>

                {/* Recategorization (admin) / Report category (non-admin) */}
                {canRecategorize ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Recategorize product (persisted)
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel>Category group</InputLabel>
                        <Select
                          value={recategorizeCategoryGroup || ''}
                          label="Category group"
                          onChange={(e) => setRecategorizeCategoryGroup(e.target.value)}
                        >
                          {recategorizeCategoryOptions.map((group) => (
                            <MenuItem key={group} value={group}>
                              {group}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        onClick={handleSaveRecategorization}
                        disabled={isSavingRecategorization || !selectedProductDetails?.title}
                      >
                        {isSavingRecategorization ? 'Saving…' : 'Save'}
                      </Button>
                      {recategorizationError && (
                        <Typography variant="body2" color="error">
                          {recategorizationError}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Category group
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      {getEffectiveCategoryGroup(selectedProductDetails) || 'N/A'}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenReportDialog('wrong_category')}
                      disabled={!selectedProductDetails?.title}
                    >
                      Report wrong category
                    </Button>
                  </Box>
                )}

                {isAdmin ? (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Brand (persisted)
                    </Typography>
                    {selectedProductDetails?.title && brandOverrides[selectedProductDetails.title] !== undefined && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Overridden
                        {selectedProductDetails.brand
                          ? ` (dataset: ${selectedProductDetails.brand})`
                          : ' (no brand in dataset)'}
                      </Typography>
                    )}
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={brandSelectOptions}
                        value={editBrandValue}
                        onChange={(_, newValue) => setEditBrandValue(newValue ?? '')}
                        onInputChange={(_, newInput) => setEditBrandValue(newInput)}
                        renderInput={(params) => (
                          <TextField {...params} label="Brand" placeholder="Select or type brand" />
                        )}
                        sx={{ minWidth: 240, flex: 1 }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => handleSaveBrandOverride()}
                        disabled={isSavingBrand || !selectedProductDetails?.title}
                      >
                        {isSavingBrand ? 'Saving…' : 'Save brand'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleResetBrandOverride}
                        disabled={
                          isSavingBrand ||
                          !selectedProductDetails?.title ||
                          brandOverrides[selectedProductDetails.title] === undefined
                        }
                      >
                        Reset to dataset
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => handleSaveBrandOverride('')}
                        disabled={isSavingBrand || !selectedProductDetails?.title}
                      >
                        Clear brand
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Custom brands (available in dropdown)
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        label="New brand name"
                        value={newCustomBrandInput}
                        onChange={(e) => setNewCustomBrandInput(e.target.value)}
                        sx={{ minWidth: 200 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleAddCustomBrand}
                        disabled={isSavingCustomBrand}
                      >
                        Add brand
                      </Button>
                    </Box>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {customBrands.length === 0 ? (
                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          None
                        </Typography>
                      ) : (
                        customBrands.map((brand) => (
                          <Chip
                            key={brand}
                            label={brand}
                            size="small"
                            onDelete={() => handleRemoveCustomBrand(brand)}
                            disabled={isSavingCustomBrand}
                          />
                        ))
                      )}
                    </Box>
                    {brandEditError && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {brandEditError}
                      </Typography>
                    )}
                  </Box>
                ) : null}

                {/* SKU visibility (listing audience) — independent from price-type / promo flags */}
                {canRecategorize ? (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      {PRODUCT_FLAG_LABEL_SKU_VISIBILITY}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 260 }}>
                      <InputLabel>{PRODUCT_FLAG_LABEL_SKU_VISIBILITY}</InputLabel>
                      <Select
                        value={currentSkuVisibility || SKU_VISIBILITY_DEFAULT}
                        label={PRODUCT_FLAG_LABEL_SKU_VISIBILITY}
                        onChange={(e) => setSkuVisibility(e.target.value)}
                        disabled={!selectedProductDetails?.title}
                      >
                        {SKU_VISIBILITY_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: 'block' }}>
                      Who can see this SKU on the retailer website. Default is For everyone.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      {PRODUCT_FLAG_LABEL_SKU_VISIBILITY}
                    </Typography>
                    <Chip
                      label={getSkuVisibilityLabel(currentSkuVisibility)}
                      size="small"
                      sx={SKU_VISIBILITY_CHIP_SX[currentSkuVisibility] || SKU_VISIBILITY_CHIP_SX[SKU_VISIBILITY_DEFAULT]}
                    />
                  </Box>
                )}

                {/* Price type annotation (admin) / Report (non-admin) */}
                {isAdmin ? (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Price type annotation
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        label="Starter Kit / Registration Price"
                        onClick={() => toggleProductFlag('isStarterKitOrRegistration')}
                        variant={currentFlags.isStarterKitOrRegistration ? 'filled' : 'outlined'}
                        sx={currentFlags.isStarterKitOrRegistration
                          ? { bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, border: '1px solid #ffcc80', cursor: 'pointer' }
                          : { cursor: 'pointer', color: 'text.secondary' }}
                      />
                      <Chip
                        label="Limited edition"
                        onClick={() => setPortfolioType(currentFlags.portfolioType === 'Limited edition' ? null : 'Limited edition')}
                        variant={currentFlags.portfolioType === 'Limited edition' ? 'filled' : 'outlined'}
                        sx={currentFlags.portfolioType === 'Limited edition'
                          ? { bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, border: '1px solid #81c784', cursor: 'pointer' }
                          : { cursor: 'pointer', color: 'text.secondary' }}
                      />
                      <Chip
                        label="Promo offer"
                        onClick={() => toggleProductFlag('isPromoOffer')}
                        variant={currentFlags.isPromoOffer ? 'filled' : 'outlined'}
                        sx={currentFlags.isPromoOffer
                          ? { bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 600, border: '1px solid #ce93d8', cursor: 'pointer' }
                          : { cursor: 'pointer', color: 'text.secondary' }}
                      />
                      <Chip
                        label={PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY}
                        onClick={() => toggleProductFlag('isForNewUsersOnly')}
                        variant={currentFlags.isForNewUsersOnly ? 'filled' : 'outlined'}
                        sx={currentFlags.isForNewUsersOnly
                          ? { ...FOR_NEW_USERS_ONLY_CHIP_SX, cursor: 'pointer' }
                          : { cursor: 'pointer', color: 'text.secondary' }}
                      />
                      <Chip
                        label="Customizable"
                        onClick={() => toggleProductFlag('isCustomFlag')}
                        variant={currentFlags.isCustomFlag ? 'filled' : 'outlined'}
                        sx={currentFlags.isCustomFlag
                          ? { bgcolor: '#e3f2fd', color: '#0d47a1', fontWeight: 600, border: '1px solid #90caf9', cursor: 'pointer' }
                          : { cursor: 'pointer', color: 'text.secondary' }}
                      />
                      <Chip
                        label={PRODUCT_FLAG_LABEL_PRICE_BANNER}
                        variant={currentFlags[PRODUCT_FLAG_PRICE_BANNER] ? 'filled' : 'outlined'}
                        sx={currentFlags[PRODUCT_FLAG_PRICE_BANNER]
                          ? { ...PRICE_BANNER_CHIP_SX, cursor: 'default' }
                          : { cursor: 'default', color: 'text.secondary' }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: 'block' }}>
                      Click to toggle. Persisted to the shared product flags store.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Price type annotation
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                      <Chip
                        label="Starter Kit / Registration Price"
                        variant={currentFlags.isStarterKitOrRegistration ? 'filled' : 'outlined'}
                        sx={currentFlags.isStarterKitOrRegistration
                          ? { bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, border: '1px solid #ffcc80' }
                          : { color: 'text.secondary' }}
                      />
                      <Chip
                        label="Limited edition"
                        variant={currentFlags.portfolioType === 'Limited edition' ? 'filled' : 'outlined'}
                        sx={currentFlags.portfolioType === 'Limited edition'
                          ? { bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, border: '1px solid #81c784' }
                          : { color: 'text.secondary' }}
                      />
                      <Chip
                        label="Promo offer"
                        variant={currentFlags.isPromoOffer ? 'filled' : 'outlined'}
                        sx={currentFlags.isPromoOffer
                          ? { bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 600, border: '1px solid #ce93d8' }
                          : { color: 'text.secondary' }}
                      />
                      <Chip
                        label={PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY}
                        variant={currentFlags.isForNewUsersOnly ? 'filled' : 'outlined'}
                        sx={currentFlags.isForNewUsersOnly ? FOR_NEW_USERS_ONLY_CHIP_SX : { color: 'text.secondary' }}
                      />
                      <Chip
                        label="Customizable"
                        variant={currentFlags.isCustomFlag ? 'filled' : 'outlined'}
                        sx={currentFlags.isCustomFlag
                          ? { bgcolor: '#e3f2fd', color: '#0d47a1', fontWeight: 600, border: '1px solid #90caf9' }
                          : { color: 'text.secondary' }}
                      />
                    </Box>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenReportDialog('wrong_price_type')}
                        disabled={!selectedProductDetails?.title}
                      >
                        Report wrong price type
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenReportDialog('other')}
                        disabled={!selectedProductDetails?.title}
                      >
                        Report anything else
                      </Button>
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Pricing Information */}
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5} sx={{ mb: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Pricing & Availability
                  </Typography>
                  {availablePricingDates.length > 0 && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Prices as of"
                        value={pricingAsOfDateKey ? dayjs(pricingAsOfDateKey) : null}
                        onChange={(newValue) => {
                          if (newValue?.isValid()) {
                            setPricingAsOfDateKey(newValue.format('YYYY-MM-DD'));
                          }
                        }}
                        minDate={dayjs(availablePricingDates[0])}
                        maxDate={dayjs(
                          dateFromUrl && dateFromUrl > availablePricingDates[availablePricingDates.length - 1]
                            ? dateFromUrl
                            : availablePricingDates[availablePricingDates.length - 1]
                        )}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: {
                              width: 200,
                              '& .MuiInputBase-root': { height: 32, fontSize: '0.75rem' },
                              '& .MuiInputBase-input': { padding: '4px 8px' },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Box>
                {pricingDetails?.pricing_observation_count > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Averaged across {pricingDetails.pricing_observation_count} retailer observations on{' '}
                    {pricingAsOfDateKey
                      ? dayjs(pricingAsOfDateKey).format('D. M. YYYY')
                      : 'selected date'}
                  </Typography>
                )}
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CurrencyDollar size={16} color="#4CAF50" />
                      <Typography variant="body2">
                        <strong>Currency:</strong> {pricingDetails?.currency || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CurrencyDollar size={16} color="#FF9800" />
                      <Typography variant="body2">
                        <strong>Actual Price:</strong> {pricingDetails?.actual_price || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CurrencyDollar size={16} color="#2196F3" />
                      <Typography variant="body2">
                        <strong>Regular Price:</strong> {pricingDetails?.regular_price || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Package size={16} color={/in stock/i.test(pricingDetails?.stock_status || '') ? '#4CAF50' : '#f44336'} />
                      <Typography variant="body2">
                        <strong>Stock:</strong> {pricingDetails?.stock_status || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {packagingOffers.length > 0 && (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Listed packaging
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                      From package_variants in the dataset. Informational only — does not set pack size.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {packagingOffers.map((offer) => {
                        const currency = offer.currency || pricingDetails?.currency;
                        return (
                          <Box
                            key={offer.label}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
                          >
                            <Chip
                              label={formatPackageVariantLabel(offer.label)}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                            <Box display="flex" gap={2} flexWrap="wrap">
                              <Typography variant="body2">
                                <strong>Actual:</strong>{' '}
                                <span style={{ color: '#FF9800' }}>{offer.actual_price || 'N/A'}</span>
                                {currency && offer.actual_price ? ` ${currency}` : ''}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Regular:</strong>{' '}
                                <span style={{ color: '#2196F3' }}>{offer.regular_price || 'N/A'}</span>
                                {currency && offer.regular_price ? ` ${currency}` : ''}
                              </Typography>
                              {offer.units ? (
                                <Typography variant="body2" color="text.secondary">
                                  Listed units: {offer.units}
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Pack Size
                    </Typography>
                    {isAdmin && (
                      <IconButton
                        size="small"
                        onClick={addPackAssignmentRow}
                        aria-label="Add pack size"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {isAdmin ? (
                    <PackSizePicker
                      options={PACK_SIZE_OPTIONS}
                      selectedPackSize={selectedPackSize}
                      customPackSize={customPackSize}
                      onSelect={(val) => savePackSizeOverride(val, val === 'other' ? customPackSize : '')}
                      onCustomChange={(custom) => savePackSizeOverride('other', custom)}
                    />
                  ) : (
                    <Typography variant="body2">
                      {formatPackSizeLabel(selectedPackSize, customPackSize)}
                    </Typography>
                  )}
                  <PackAssignmentPrices
                    actualPrice={pricingDetails?.actual_price}
                    regularPrice={pricingDetails?.regular_price}
                    currency={pricingDetails?.currency}
                    packUnits={effectivePackSize}
                  />
                  {extraPackAssignments.map((row, index) => {
                    const packUnits = numericFromPackAssignment(row.packSize, row.customPackSize);
                    return (
                      <Box
                        key={row.id}
                        sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Pack size {index + 2}
                          </Typography>
                          {isAdmin && (
                            <IconButton
                              size="small"
                              onClick={() => removeExtraPackAssignment(row.id)}
                              aria-label="Remove pack size"
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        {isAdmin ? (
                          <PackSizePicker
                            options={PACK_SIZE_OPTIONS}
                            selectedPackSize={row.packSize}
                            customPackSize={row.customPackSize || ''}
                            onSelect={(val) =>
                              updateExtraPackAssignment(row.id, val, val === 'other' ? row.customPackSize || '' : '')
                            }
                            onCustomChange={(custom) => updateExtraPackAssignment(row.id, 'other', custom)}
                          />
                        ) : (
                          <Typography variant="body2">
                            {formatPackSizeLabel(row.packSize, row.customPackSize)}
                          </Typography>
                        )}
                        <PackAssignmentPrices
                          actualPrice={pricingDetails?.actual_price}
                          regularPrice={pricingDetails?.regular_price}
                          currency={pricingDetails?.currency}
                          packUnits={packUnits}
                        />
                      </Box>
                    );
                  })}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Additional Information */}
                <Typography variant="h6" gutterBottom>
                  Additional Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Product ID:</strong> {selectedProductDetails.id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Position:</strong> {selectedProductDetails.position || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>URL:</strong>
                      {selectedProductDetails.url && selectedProductDetails.url !== 'N/A' ? (
                        <span style={{ marginLeft: 8 }}>
                          <a
                            href={selectedProductDetails.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1976d2' }}
                          >
                            Real product
                          </a>
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  color={isDescriptionMissing ? 'text.secondary' : 'text.primary'}
                  sx={{
                    whiteSpace: descriptionExpanded ? 'pre-wrap' : 'nowrap',
                    overflow: descriptionExpanded ? 'visible' : 'hidden',
                    textOverflow: descriptionExpanded ? 'clip' : 'ellipsis',
                  }}
                >
                  {productDescription}
                </Typography>
                {canExpandDescription && (
                  <Button
                    size="small"
                    onClick={() => setDescriptionExpanded((expanded) => !expanded)}
                    endIcon={descriptionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mt: 0.5, px: 0, minWidth: 0, textTransform: 'none' }}
                  >
                    {descriptionExpanded ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" color="text.secondary" align="center">
              {selectedBrand ? 'Select a product to view details' : 'Select a brand and product to view details'}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
    <Dialog
      open={priceBannerDialogOpen}
      onClose={() => !generatingPriceBanner && setPriceBannerDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Generate Price banner product</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a scraped price banner, review the details, then create a product that appears in this view.
        </Typography>
        {priceBannersLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : priceBannersError ? (
          <Alert severity="error">{priceBannersError}</Alert>
        ) : priceBanners.length === 0 ? (
          <Alert severity="info">No banners found in the price_banners folder{selectedCountry ? ` for ${selectedCountry}` : ''}.</Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Box sx={{ maxHeight: 420, overflow: 'auto', pr: 0.5 }}>
                {priceBanners.map((banner) => {
                  const selected = selectedPriceBanner?.banner_key === banner.banner_key;
                  return (
                    <Box
                      key={banner.banner_key}
                      onClick={() => selectPriceBanner(banner)}
                      sx={{
                        mb: 1,
                        p: 1,
                        border: '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'action.selected' : 'background.paper',
                        borderRadius: 1,
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {banner.text || banner.suggested_title || 'Untitled banner'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {(banner.country || '').toUpperCase()}
                        {banner.retailer ? ` · ${banner.retailer}` : ''}
                        {banner.date ? ` · ${banner.date}` : ''}
                        {banner.extracted_price != null
                          ? ` · ${banner.extracted_price} ${banner.extracted_currency || ''}`.trim()
                          : ''}
                      </Typography>
                      {banner.already_generated && (
                        <Chip
                          size="small"
                          label="Already generated"
                          sx={{ mt: 0.5, ...PRICE_BANNER_CHIP_SX }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              {selectedPriceBanner ? (
                <Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 180,
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      overflow: 'hidden',
                      mb: 2,
                    }}
                  >
                    {(selectedPriceBanner.image || selectedPriceBanner.image_mobile) ? (
                      <Box
                        component="img"
                        src={selectedPriceBanner.image || selectedPriceBanner.image_mobile}
                        alt={selectedPriceBanner.text || 'Banner'}
                        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <ImageIcon size={40} color="#ccc" />
                        <Typography variant="caption" color="text.secondary">No image</Typography>
                      </Box>
                    )}
                  </Box>
                  {selectedPriceBanner.description && (
                    <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                      {selectedPriceBanner.description}
                    </Typography>
                  )}
                  {selectedPriceBanner.cta_text && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      CTA: {selectedPriceBanner.cta_text}
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    label="Product title"
                    value={priceBannerTitle}
                    onChange={(e) => setPriceBannerTitle(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  <Box display="flex" gap={1.5}>
                    <TextField
                      size="small"
                      label="Price"
                      value={priceBannerPrice}
                      onChange={(e) => setPriceBannerPrice(e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Currency"
                      value={priceBannerCurrency}
                      onChange={(e) => setPriceBannerCurrency(e.target.value)}
                      sx={{ width: 120 }}
                    />
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a banner to preview.
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPriceBannerDialogOpen(false)} disabled={generatingPriceBanner}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGeneratePriceBannerProduct}
          disabled={generatingPriceBanner || !selectedPriceBanner || !priceBannerTitle.trim()}
        >
          {generatingPriceBanner
            ? 'Generating…'
            : selectedPriceBanner?.already_generated
              ? 'Open / update product'
              : 'Generate product'}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={reportDialogOpen} onClose={handleCloseReportDialog} maxWidth="sm" fullWidth>
      <DialogTitle>{reportDialogTitle}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe the issue below. A support ticket will be created for the team to review.
        </Typography>
        {selectedProductDetails?.title && (
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            <SkuTitleWithTranslate
              title={selectedProductDetails.title}
              country={selectedProductDetails.country}
              getDisplayTitle={getSkuDisplayTitle}
              isTranslationActive={isSkuTranslationActive}
              isTranslatingTitle={isSkuTitleTranslating}
              onToggleTranslate={toggleSkuTitleTranslation}
            />
          </Typography>
        )}
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={5}
          label="Details"
          value={reportDescription}
          onChange={(e) => setReportDescription(e.target.value)}
          variant="outlined"
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseReportDialog} disabled={reportSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" color="warning" onClick={handleSubmitReport} disabled={reportSubmitting}>
          {reportSubmitting ? 'Submitting…' : 'Submit report'}
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      open={reportSnackbar.open}
      autoHideDuration={4000}
      onClose={() => setReportSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setReportSnackbar((s) => ({ ...s, open: false }))}
        severity={reportSnackbar.severity}
        variant="filled"
      >
        {reportSnackbar.message}
      </Alert>
    </Snackbar>
  </>
  );
}

export default ProductDetailView; 